import { Link } from "wouter";
import type { Job } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";

// Schedule beyond one day: a week (a column per day, or techs × days) and a
// month grid. Jobs open on tap; a day header opens that day's dispatch board.

type JobRow = Job & { id: string };
const DAY = 86_400_000;
const time = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
const STATUS_BAR: Record<string, string> = {
  scheduled: "border-l-steel",
  in_progress: "border-l-brandorange",
  done: "border-l-success",
  canceled: "border-l-steel/30",
};

function dayStarts(start: number, n: number): number[] {
  const out: number[] = [];
  const d = new Date(start);
  for (let i = 0; i < n; i++) out.push(new Date(d.getFullYear(), d.getMonth(), d.getDate() + i).getTime());
  return out;
}
const onDay = (jobs: JobRow[], dayStart: number) => {
  const next = new Date(dayStart);
  const end = new Date(next.getFullYear(), next.getMonth(), next.getDate() + 1).getTime();
  return jobs.filter((j) => j.startAt && j.startAt >= dayStart && j.startAt < end).sort((a, b) => a.startAt! - b.startAt!);
};
const isToday = (dayStart: number) => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime() === dayStart;
};

function JobChip({ j, memberName, compact = false }: { j: JobRow; memberName: (uid: string) => string; compact?: boolean }) {
  const techs = j.assignedUids.map((u) => memberName(u).split(/\s+/)[0]).join(", ");
  return (
    <Link
      href={`/jobs/${j.id}`}
      className={cn(
        "block rounded-md border border-l-4 border-steel/15 bg-white px-1.5 py-1 text-[11px] leading-tight hover:border-brandorange/50",
        STATUS_BAR[j.status],
        j.status === "canceled" && "line-through opacity-60",
      )}
      title={`${j.customerName ? `${j.customerName} · ` : ""}${j.title}`}
    >
      <span className="font-semibold text-ink">{time(j.startAt!)}</span>{" "}
      <span className="text-ink">{j.customerName || j.title}</span>
      {!compact ? (
        <span className={cn("block truncate", techs ? "text-steel" : "font-semibold text-brandorange-dark")}>{techs || "No tech"}</span>
      ) : null}
    </Link>
  );
}

export function WeekView({
  weekStart,
  jobs,
  members,
  memberName,
  layout,
  onOpenDay,
}: {
  weekStart: number;
  jobs: JobRow[];
  members: Array<{ uid: string; name: string }>;
  memberName: (uid: string) => string;
  layout: "calendar" | "techs" | "days";
  onOpenDay: (dayStart: number) => void;
}) {
  const days = dayStarts(weekStart, 7);
  const working = members.filter((m) => jobs.some((j) => j.assignedUids.includes(m.uid)));
  const unassigned = jobs.filter((j) => j.assignedUids.length === 0);
  const lanes = [...working.map((m) => ({ uid: m.uid, name: m.name, list: jobs.filter((j) => j.assignedUids.includes(m.uid)) })), ...(unassigned.length ? [{ uid: "", name: "No tech yet", list: unassigned }] : [])];
  const head = (d: number) => (
    <button
      className={cn("w-full rounded-md px-1 py-1 text-left text-xs hover:bg-cream", isToday(d) ? "font-bold text-brandorange-dark" : "font-semibold text-ink")}
      onClick={() => onOpenDay(d)}
      title="Open this day's dispatch board"
    >
      {new Date(d).toLocaleDateString(undefined, { weekday: "short" })} {new Date(d).getDate()}
      <span className="ml-1 font-normal text-steel">({onDay(jobs, d).length})</span>
    </button>
  );

  // Flipped: a row per day, a column per tech (dates down the side, names across the top).
  if (layout === "days") {
    const cols = lanes.length || 1;
    return (
      <div className="overflow-x-auto rounded-xl border border-steel/25 bg-white">
        <div className="grid" style={{ gridTemplateColumns: `120px repeat(${cols}, minmax(150px, 1fr))`, minWidth: 120 + cols * 150 }}>
          <div className="border-b border-steel/15 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-steel">Day</div>
          {lanes.length ? (
            lanes.map((l) => (
              <div key={l.uid || "none"} className={cn("truncate border-b border-l border-steel/15 px-2 py-1.5 text-xs font-semibold", l.uid ? "text-ink" : "text-brandorange-dark")}>
                {l.name} <span className="font-normal text-steel">({l.list.length})</span>
              </div>
            ))
          ) : (
            <div className="border-b border-l border-steel/15 px-2 py-1.5 text-xs text-steel">Nobody scheduled</div>
          )}
          {days.map((d) => (
            <div key={d} className="contents">
              <div className={cn("border-b border-steel/10", isToday(d) && "bg-brandorange/5")}>{head(d)}</div>
              {(lanes.length ? lanes : [{ uid: "", name: "", list: [] as JobRow[] }]).map((l) => (
                <div key={`${d}:${l.uid}`} className={cn("space-y-1 border-b border-l border-steel/10 p-1", isToday(d) && "bg-brandorange/5")}>
                  {onDay(l.list, d).map((j) => (
                    <JobChip key={j.id} j={j} memberName={memberName} compact />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (layout === "techs") {
    return (
      <div className="overflow-x-auto rounded-xl border border-steel/25 bg-white">
        <div className="grid min-w-[760px] grid-cols-[140px_repeat(7,minmax(0,1fr))]">
          <div className="border-b border-steel/15 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-steel">Tech</div>
          {days.map((d) => (
            <div key={d} className={cn("border-b border-l border-steel/15", isToday(d) && "bg-brandorange/5")}>
              {head(d)}
            </div>
          ))}
          {lanes.map((row) => (
            <div key={row.uid || "none"} className="contents">
              <div className={cn("truncate border-b border-steel/10 px-2 py-1.5 text-xs font-medium", row.uid ? "text-ink" : "text-brandorange-dark")}>{row.name}</div>
              {days.map((d) => (
                <div key={d} className={cn("space-y-1 border-b border-l border-steel/10 p-1", isToday(d) && "bg-brandorange/5")}>
                  {onDay(row.list, d).map((j) => (
                    <JobChip key={j.id} j={j} memberName={memberName} compact />
                  ))}
                </div>
              ))}
            </div>
          ))}
          {working.length === 0 && unassigned.length === 0 ? <div className="col-span-8 px-4 py-8 text-center text-sm text-steel">Nothing scheduled this week.</div> : null}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Wide screens: seven columns. */}
      <div className="hidden grid-cols-7 gap-1 md:grid">
        {days.map((d) => (
          <div key={d} className={cn("min-h-40 rounded-xl border border-steel/20 bg-white p-1", isToday(d) && "border-brandorange/50 bg-brandorange/5")}>
            {head(d)}
            <div className="space-y-1">
              {onDay(jobs, d).map((j) => (
                <JobChip key={j.id} j={j} memberName={memberName} />
              ))}
              {onDay(jobs, d).length === 0 ? <div className="px-1 py-2 text-[11px] text-steel/70">Nothing</div> : null}
            </div>
          </div>
        ))}
      </div>
      {/* Phones: day by day. */}
      <div className="space-y-2 md:hidden">
        {days.map((d) => (
          <div key={d} className={cn("rounded-xl border border-steel/20 bg-white p-2", isToday(d) && "border-brandorange/50")}>
            {head(d)}
            <div className="space-y-1">
              {onDay(jobs, d).map((j) => (
                <JobChip key={j.id} j={j} memberName={memberName} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function MonthView({ monthStart, jobs, memberName, onOpenDay }: { monthStart: number; jobs: JobRow[]; memberName: (uid: string) => string; onOpenDay: (dayStart: number) => void }) {
  const first = new Date(monthStart);
  const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay()).getTime();
  const cells = dayStarts(gridStart, 42);
  const month = first.getMonth();
  return (
    <div className="rounded-xl border border-steel/25 bg-white p-1">
      <div className="grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-steel">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
          <div key={w} className="py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-steel/10">
        {cells.map((d) => {
          const list = onDay(jobs, d);
          const inMonth = new Date(d).getMonth() === month;
          return (
            <div key={d} className={cn("min-h-24 bg-white p-1", !inMonth && "bg-cream/40", isToday(d) && "bg-brandorange/5")}>
              <button
                className={cn("mb-0.5 flex w-full items-center justify-between rounded px-1 text-xs hover:bg-cream", isToday(d) ? "font-bold text-brandorange-dark" : inMonth ? "text-ink" : "text-steel/60")}
                onClick={() => onOpenDay(d)}
                title="Open this day's dispatch board"
              >
                <span>{new Date(d).getDate()}</span>
                {list.length ? <span className="text-[10px] font-normal text-steel">{list.length}</span> : null}
              </button>
              <div className="hidden space-y-0.5 sm:block">
                {list.slice(0, 3).map((j) => (
                  <JobChip key={j.id} j={j} memberName={memberName} compact />
                ))}
                {list.length > 3 ? (
                  <button className="w-full px-1 text-left text-[10px] text-steel hover:text-ink" onClick={() => onOpenDay(d)}>
                    +{list.length - 3} more
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const STATUS_WORD: Record<string, string> = { scheduled: "", in_progress: "In progress", done: "Done", canceled: "Canceled" };

function UpcomingRow({ j, memberName, when }: { j: JobRow; memberName: (uid: string) => string; when: string }) {
  const techs = j.assignedUids.map((u) => memberName(u)).join(", ");
  return (
    <Link
      href={`/jobs/${j.id}`}
      className={cn("flex min-h-9 items-center gap-3 border-b border-l-4 border-b-steel/10 px-3 py-1.5 text-sm last:border-b-0 hover:bg-cream/60", STATUS_BAR[j.status])}
    >
      <span className="w-16 shrink-0 text-xs font-semibold text-ink">{when}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ink">{j.customerName ? `${j.customerName} · ${j.title}` : j.title}</span>
        {j.address ? <span className="block truncate text-xs text-steel">{j.address}</span> : null}
      </span>
      <span className={cn("hidden w-40 shrink-0 truncate text-right text-xs sm:block", techs ? "text-steel" : "font-semibold text-brandorange-dark")}>{techs || "No tech"}</span>
      {STATUS_WORD[j.status] ? <span className="shrink-0 text-xs text-steel">{STATUS_WORD[j.status]}</span> : null}
    </Link>
  );
}

/** Upcoming: the next stretch of days as one list, day by day, with what has no date yet on top. */
export function UpcomingView({
  fromDay,
  days,
  jobs,
  dateless,
  memberName,
  onMore,
  onOpenDay,
}: {
  fromDay: number;
  days: number;
  jobs: JobRow[];
  dateless: JobRow[];
  memberName: (uid: string) => string;
  onMore: () => void;
  onOpenDay: (dayStart: number) => void;
}) {
  const groups = dayStarts(fromDay, days)
    .map((d) => ({ d, items: onDay(jobs, d) }))
    .filter((g) => g.items.length);
  const label = (d: number) => {
    const tomorrow = new Date(fromDay);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const name = new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    return isToday(d) ? `Today · ${name}` : d === tomorrow.getTime() && isToday(fromDay) ? `Tomorrow · ${name}` : name;
  };
  const box = "overflow-hidden rounded-xl bg-white ring-1 ring-steel/15";
  return (
    <div className="space-y-3">
      {dateless.length ? (
        <div className={box}>
          <div className="border-b border-steel/10 bg-brandorange/10 px-3 py-1.5 text-xs font-semibold text-ink">
            No date yet ({dateless.length}): pick a day on the job, or assign it from the Day board
          </div>
          {dateless.map((j) => (
            <UpcomingRow key={j.id} j={j} memberName={memberName} when="—" />
          ))}
        </div>
      ) : null}
      {groups.map((g) => (
        <div key={g.d} className={box}>
          <button className="flex w-full items-center justify-between border-b border-steel/10 px-3 py-1.5 text-left text-xs font-semibold text-ink hover:bg-cream/60" onClick={() => onOpenDay(g.d)}>
            <span className={isToday(g.d) ? "text-brandorange-dark" : ""}>{label(g.d)}</span>
            <span className="font-normal text-steel">
              {g.items.length} job{g.items.length === 1 ? "" : "s"} · open the day →
            </span>
          </button>
          {g.items.map((j) => (
            <UpcomingRow key={j.id} j={j} memberName={memberName} when={time(j.startAt!)} />
          ))}
        </div>
      ))}
      {groups.length === 0 && dateless.length === 0 ? <p className="py-6 text-center text-sm text-steel">Nothing on the schedule for the next {days} days.</p> : null}
      <button className="block w-full rounded-lg py-2 text-center text-xs text-steel hover:text-ink" onClick={onMore}>
        Show two more weeks
      </button>
    </div>
  );
}

export { DAY as RANGE_DAY };
